import React from "react";
import BannerImage from "../../../assets/images/banner.png";
import Banner from "../banner";

const BRIGHT_AIRWAYS_URL = "https://www.brightairways.in/";

const Main = () => {
  return (
    <>
      <Banner
        img={BannerImage}
        title="How to Start a Career in Aviation in India"
      />
      <div className="container mt-4 mb-5">
        <article className="blog-article">
          <header className="mb-4">
            <h3 className="fw-bold">Introduction to Aviation Careers</h3>
            <p>
              The aviation industry in India is growing rapidly, creating
              thousands of job opportunities every year. If you&apos;ve ever
              dreamed of working at airports or flying across the world, now is
              the perfect time to explore{" "}
              <a
                href={BRIGHT_AIRWAYS_URL}
                target="_blank"
                rel="noopener noreferrer"
              >
                how to start a career in aviation in India
              </a>
              .
            </p>
            <p>
              From cabin crew to ground staff and ticketing agents, aviation
              offers exciting roles with global exposure and career growth. The
              industry values professionalism, communication, and customer
              service—making it ideal for young professionals.
            </p>
          </header>

          <section className="mb-4">
            <h3 className="fw-semibold">Why Choose Aviation as a Career</h3>
            <p>
              Aviation is one of the most attractive career fields today.
              Here&apos;s why:
            </p>
            <ul>
              <li>✈️ Opportunity to travel worldwide</li>
              <li>💼 Stable and growing industry</li>
              <li>💰 Competitive salaries</li>
              <li>🌍 Exposure to international culture</li>
              <li>🚀 Fast career growth</li>
            </ul>
            <p>
              It&apos;s not just a job—it&apos;s a lifestyle that offers
              adventure and prestige.
            </p>
          </section>

          <section className="mb-4">
            <h3 className="fw-semibold">Types of Jobs in Aviation</h3>

            <h5 className="fw-semibold mt-3">✈️ Cabin Crew Jobs</h5>
            <p>
              Cabin crew members ensure passenger safety and comfort during
              flights. Their responsibilities include:
            </p>
            <ul>
              <li>Assisting passengers</li>
              <li>Demonstrating safety procedures</li>
              <li>Providing in-flight services</li>
            </ul>

            <h5 className="fw-semibold mt-3">🏢 Ground Staff Jobs</h5>
            <p>Ground staff work at airports and handle operations like:</p>
            <ul>
              <li>Check-in and boarding</li>
              <li>Baggage handling</li>
              <li>Customer assistance</li>
            </ul>

            <h5 className="fw-semibold mt-3">🎫 Ticketing &amp; Customer Service</h5>
            <p>These professionals manage bookings and reservations:</p>
            <ul>
              <li>Ticket sales</li>
              <li>Flight scheduling</li>
              <li>Customer queries</li>
            </ul>
          </section>

          <section className="mb-4">
            <h3 className="fw-semibold">Eligibility Criteria</h3>
            <p>To start your journey in aviation, you need:</p>
            <ul>
              <li>Minimum 12th pass (any stream)</li>
              <li>Good communication skills</li>
              <li>Basic English proficiency</li>
              <li>Age: Typically 18–27 years</li>
            </ul>
            <p>
              Some roles may require graduation, especially for higher
              positions.
            </p>
          </section>

          <section className="mb-4">
            <h3 className="fw-semibold">Skills Required for Aviation Careers</h3>
            <p>
              To succeed in aviation, you need a mix of soft and professional
              skills:
            </p>
            <p>
              <strong>Essential Skills:</strong>
            </p>
            <ul>
              <li>Communication skills</li>
              <li>Customer service attitude</li>
              <li>Confidence and personality</li>
              <li>Problem-solving ability</li>
              <li>Teamwork</li>
            </ul>
            <div className="blog-tip">
              These skills are more important than academic qualifications in
              many roles.
            </div>
          </section>

          <section className="mb-4">
            <h3 className="fw-semibold">Step-by-Step Guide to Start Your Career</h3>
            <p>Here&apos;s a simple roadmap:</p>

            <h5 className="fw-semibold mt-3">Step 1: Choose Your Career Path</h5>
            <p>Decide whether you want to be:</p>
            <ul>
              <li>Cabin crew</li>
              <li>Ground staff</li>
              <li>Ticket agent</li>
            </ul>

            <h5 className="fw-semibold mt-3">Step 2: Meet Basic Requirements</h5>
            <p>Ensure you meet:</p>
            <ul>
              <li>Age criteria</li>
              <li>Education level</li>
              <li>Physical fitness</li>
            </ul>

            <h5 className="fw-semibold mt-3">Step 3: Get Training (If Needed)</h5>
            <p>Some roles require training in:</p>
            <ul>
              <li>Grooming</li>
              <li>Communication</li>
              <li>Aviation basics</li>
            </ul>

            <h5 className="fw-semibold mt-3">Step 4: Apply for Jobs</h5>
            <p>Apply through:</p>
            <ul>
              <li>Official company websites</li>
              <li>
                Verified aviation platforms like{" "}
                <a
                  href={BRIGHT_AIRWAYS_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Bright Airways
                </a>
              </li>
            </ul>

            <h5 className="fw-semibold mt-3">Step 5: Attend Interviews</h5>
            <p>Prepare for:</p>
            <ul>
              <li>HR interviews</li>
              <li>Group discussions</li>
              <li>Personality assessments</li>
            </ul>

            <h5 className="fw-semibold mt-3">Step 6: Start Your Career</h5>
            <p>
              Once selected, begin your journey in the aviation industry.
            </p>
          </section>

          <section className="mb-4">
            <h3 className="fw-semibold">Training and Courses</h3>
            <p>
              While not always mandatory, training can increase your chances of
              selection.
            </p>
            <p>Popular training areas include:</p>
            <ul>
              <li>Cabin crew training</li>
              <li>Hospitality management</li>
              <li>Aviation customer service</li>
            </ul>
            <p>
              Training helps you gain confidence and industry knowledge. Explore
              aviation training and career guidance at{" "}
              <a
                href={BRIGHT_AIRWAYS_URL}
                target="_blank"
                rel="noopener noreferrer"
              >
                Bright Airways
              </a>
              .
            </p>
          </section>

          <section className="mb-4">
            <h3 className="fw-semibold">Salary Expectations in Aviation</h3>
            <p>Here&apos;s a general salary overview:</p>
            <div className="table-responsive">
              <table className="blog-salary-table">
                <thead>
                  <tr>
                    <th>Job Role</th>
                    <th>Salary Range</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>Cabin Crew</td>
                    <td className="salary-amount">₹40,000 – ₹1,20,000/month</td>
                  </tr>
                  <tr>
                    <td>Ground Staff</td>
                    <td className="salary-amount">₹20,000 – ₹50,000/month</td>
                  </tr>
                  <tr>
                    <td>Ticket Agent</td>
                    <td className="salary-amount">₹25,000 – ₹40,000/month</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div className="blog-tip">
              Salaries increase with experience and skills.
            </div>
          </section>

          <section className="mb-4">
            <h3 className="fw-semibold">Challenges in Aviation Careers</h3>
            <p>
              While aviation is exciting, it also comes with challenges:
            </p>
            <ul>
              <li>Irregular working hours</li>
              <li>High competition</li>
              <li>Customer handling pressure</li>
            </ul>
            <div className="blog-tip">
              With the right mindset, these challenges can be managed.
            </div>
          </section>

          <section className="mb-4">
            <h3 className="fw-semibold">Tips for Getting Hired Faster</h3>
            <p>Follow these tips:</p>
            <ul>
              <li>Improve your communication skills</li>
              <li>Maintain a professional appearance</li>
              <li>Stay updated with industry trends</li>
              <li>Apply through trusted sources only</li>
              <li>Avoid fake job offers</li>
            </ul>
            <div className="blog-tip">
              Always verify companies before applying. Visit{" "}
              <a
                href={BRIGHT_AIRWAYS_URL}
                target="_blank"
                rel="noopener noreferrer"
              >
                brightairways.in
              </a>{" "}
              for verified aviation career support.
            </div>
          </section>

          <section className="mb-4">
            <h3 className="fw-semibold">❓ FAQs</h3>
            <div className="blog-faq-list">
              <div className="blog-faq-item">
                <h4 className="blog-faq-question">
                  1. Is aviation a good career in India?
                </h4>
                <p className="blog-faq-answer">
                  Yes, it offers growth, good salary, and global exposure.
                </p>
              </div>
              <div className="blog-faq-item">
                <h4 className="blog-faq-question">
                  2. Can I join aviation after 12th?
                </h4>
                <p className="blog-faq-answer">
                  Yes, many entry-level roles require only 12th pass.
                </p>
              </div>
              <div className="blog-faq-item">
                <h4 className="blog-faq-question">
                  3. Do I need training for aviation jobs?
                </h4>
                <p className="blog-faq-answer">
                  Not always, but training improves your chances.
                </p>
              </div>
              <div className="blog-faq-item">
                <h4 className="blog-faq-question">4. What is the age limit?</h4>
                <p className="blog-faq-answer">
                  Usually between 18 to 27 years.
                </p>
              </div>
              <div className="blog-faq-item">
                <h4 className="blog-faq-question">5. Is English necessary?</h4>
                <p className="blog-faq-answer">
                  Yes, basic English communication is important.
                </p>
              </div>
              <div className="blog-faq-item">
                <h4 className="blog-faq-question">
                  6. How long does it take to get a job?
                </h4>
                <p className="blog-faq-answer">
                  It depends on your preparation and opportunities.
                </p>
              </div>
            </div>
          </section>

          <footer className="mt-4">
            <h3 className="fw-bold">🏁 Conclusion</h3>
            <p>
              Understanding how to start a career in aviation in India is the
              first step toward an exciting future. With the right skills,
              training, and guidance, you can build a successful career in this
              fast-growing industry.
            </p>
            <p>
              Stay focused, keep learning, and take the first step today—your
              dream aviation career is waiting for you!
            </p>

            <div className="blog-cta">
              <h3>Ready to Start Your Aviation Career?</h3>
              <p>
                Get expert training, career guidance, and placement support with
                Bright Airways.
              </p>
              <a
                href={BRIGHT_AIRWAYS_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="blog-cta-btn"
              >
                Visit Bright Airways
              </a>
            </div>
          </footer>
        </article>
      </div>
    </>
  );
};

export default Main;
